import type { Messages } from '../messages.js'

/**
 * Latin. `tessera` for a tile, which is the word for exactly this object: the Romans played
 * with them. `versus`, a turning, for a flip.
 *
 * Written without macrons, to match the tiles. Length marks are a teaching aid rather than
 * spelling, no Roman wrote them, and a page that marked them while the board did not would be
 * telling the player that AMĀRE and AMARE are different words. They are not, here or anywhere.
 *
 * `versus`, `ambitus` and `ictus` are fourth declension, so their nominative is the same in
 * both numbers, which is why those plural forms look unchanged.
 */
export const la: Messages = {
  tag: 'la',

  readingDictionary: 'Lexicon legitur…',
  noWordList: 'Nullus index verborum pro "{language}". Fac unum:  pnpm dictionary build',
  emptyWordList: 'Index verborum pro "{language}" vacuus est.',

  flips: 'versus',
  score: 'puncta',
  words: 'verba',
  round: 'ambitus',
  ticksLeftLabel: 'Tempus in hoc ambitu reliquum',
  typeAWord: 'verbum scribe',
  tapPrompt: 'litteras tange ut sumas aut reddas, deinde {action}',

  boardOfTiles: 'Tabula tesserarum {n}',
  faceDown: 'aversa',
  wildCard: 'tessera vaga',
  wildKey: 'quaelibet littera',
  letterReplaced: '{from} facta est {to}',
  letterSwap: 'LITTERA MUTATA!',
  spentTile: 'tessera consumpta',
  hiddenWhilePaused: 'occulta dum intermissum est',
  letterInWord: '{letter}, littera verbi {position}',

  completeWord: 'Verbum perfice',

  completeShort: 'Perfice',
  reset: 'Dele',
  pause: 'Intermitte',
  resume: 'Perge',
  newGame: 'Novus ludus',
  paused: 'Intermissum',
  outOfFlips: 'Versus consumpti',
  finalResult: 'puncta {score} ex {words} per {rounds}',
  playAgain: 'Iterum lude',
  share: 'Communica',
  shareCopied: 'Descriptum.',
  shareSelect: 'Hoc describe:',

  lettersSelect: 'litterae sumunt',
  keysWild: 'sumitur cum litteram scribis quam nulla tessera ostendit',
  clearsEvery: 'omnes litteras {letter} lectas delet',
  undoLastLetter: 'ultimam litteram redde',
  noWordsYet: 'Nondum ulla verba.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'iam inventum',
  reasonTooShort: 'nimis breve',
  reasonNotAWord: 'non verbum',
  reasonAllFound: 'omnia iam habes',
  noSuchLetterUp: 'nulla {letter} aperta',
  nothingUp: 'non aperta',
  letterHidden: 'littera se occultavit',
  shuffled: 'permixtae',
  shuffledAndBilled: 'permixtae, versus {flips} non usi exacti',
  tooFewLetters: 'Nimis paucae litterae relictae',

  gameLanguage: 'lingua',
  interfaceLanguage: 'interfacies',
  dictionarySize: 'verba usitata {common} ex {full}',
  filterLanguages: 'Linguas quaerere',
  noMatches: 'Nihil inventum',

  nerdMode: 'modus subtilis',
  rules: 'Regulae',
  difficulty: 'difficultas',
  difficultyNames: { easy: 'facilis', medium: 'medius', hard: 'difficilis', insane: 'insanus' },
  pausedNoBoard: 'Hic ludus in tabula non ponetur.',
  about: 'De hoc ludo',
  themeLabel: 'species',
  themeNames: { traditional: 'traditus', light: 'lucidus', contrast: 'discrimen altum' },
  tiles: 'tesserae (N)',
  secondsPerTick: 'secunda / ictus',
  holdTicks: 'ictus morae',
  minWord: 'verbum brevissimum',
  startingFlips: 'versus initiales',
  wildChance: 'sors tesserae vagae',
  replaceChance: 'sors litterae mutandae',
  hideChance: 'sors litterae occultandae',
  wordCompleteMode: 'verbo perfecto',
  wordCompleteNames: { shuffle: 'permisce', spend: 'consume', keep: 'serva' },
  flipEconomy: 'ratio versuum',
  flipEconomyNames: {
    none: 'nulla',
    perLetter: 'per litteram',
    fibonacci: 'fibonacci',
    overMinimum: 'supra minimum',
  },
  repeatedLetterKey: 'clavis litterae repetitae',
  keySchemeNames: { cycle: 'orbis', advance: 'progressus' },
  keySchemeHelp: {
    cycle:
      'A proximam A non usurpatam sumit, et cum omnes in verbo sunt, eas delet. ' +
      'Shift+A quoque eas delet.',
    advance: 'A proximam A non usurpatam sumit. Shift+A omnem A in verbo delet.',
  },

  whatThatMeans: 'Quid hoc significet',
  factRound: 'ambitus',
  factWholeBoardUp: 'tota tabula aperta per',
  factRoundCosts: 'ambitus constat',
  factFlipsBuy: 'versus initiales emunt',
  factThisBoard: 'haec tabula',
  factBoardHadToAdmit: 'tabula admittere debuit',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, longissimum {longest}',
  wordsIncludingOneOf: '{words}, inter quae unum ex {ceiling}',
  scorelessRounds: '{rounds} sine punctis',

  whatAWordPays: 'Quantum verbum reddat',
  columnLetters: 'litterae',
  columnCost: 'pretium',
  columnPoints: 'puncta',
  columnFlips: 'versus',
  columnNet: 'lucrum',

  canonicalRules: 'Regulae solitae difficultatis {difficulty}.',
  customRules: 'Ex forma mutatum. Puncta sub regulis tuis in ordinem non redigentur.',
  applyAndStart: 'Adhibe et novum ludum incipe',
  changesNextGame: 'Mutationes ludo proximo valebunt.',
  presets: 'Formae:',

  start: 'Incipe',
  restart: 'Redintegra',
  quit: 'Desiste',
  quitTitle: 'Ab hoc ludo desistis?',
  restartTitle: 'Hunc ludum redintegras?',
  restartConfirm: 'Redintegra',
  quitConfirm: 'Desiste',
  keepPlaying: 'Perge ludere',
  personalBest: 'Ludi tui optimi',
  newPersonalBest: 'Novum tuum optimum.',
  columnRank: '#',
  notRanked: 'Regulae tuae, itaque hic ludus in ordinem non redigitur.',
  notRankedPaused: 'Tempus stetit, itaque hic ludus in ordinem non redigitur.',
  rankOfTotal: '{rank} ex {total}',

  howToPlay: 'Quomodo ludendum sit',

  backToGame: 'Ad ludum redi',
  welcomeTitle: 'Blinkered te salutat',
  tutorialSkip: 'Omitte',
  tutorialNext: 'Porro',
  tutorialBack: 'Retro',
  tutorialStart: 'Incipe ludere',
  tutorialHideAgain: 'Ne iterum monstretur',
  tutorialProgress: '{n} ex {total}',
  tutorialSkipTitle: 'Institutionem omittis?',
  tutGoalTitle: 'Blinkered cursus est',
  tutGoalBody:
    'Ludum incipis certo versuum numero, et quaeque tessera quae vertitur unum versum constat. Versibus consumptis ludus finitur. Verba versus merent, et quo longius verbum, eo plures versus meres.',
  tutSpentBody:
    'Litterae quas in verbo adhibes aversae manent usque ad proximum ambitum. Sola litterae mutatio mutat quae litterae in tabula sint.',
  tutChangeTitle: 'Litterae mutari possunt',
  tutSameLetters:
    'Verbum vidisti quod ludere non potuisti? Eaedem litterae proximo ambitu redeunt, in nova loca permixtae.',
  tutPickLetters: 'Litteras quas vis ordine tange, ut verbum facias.',
  tutMoreTurn: 'Tesserae dum cogitas verti pergunt, itaque littera melior adhuc venire potest.',
  tutTapBack:
    'Unam tetigisti quam nolebas? Tange iterum ut reddas. Quamlibet earum, non modo ultimam.',
  tutComplete: 'Preme Perfice cum verbum paratum est.',
  htBoardTitle: 'Tabula',
  htBoardBody: 'Tesserae singulae vertuntur, ordine fortuito. Ex tesseris apertis verba facis.',
  htWordsTitle: 'Verba',
  htWordsBody: 'Verbum ex tesseris apertis fac litteras ordine scribendo aut pulsando.',
  htFlipsTitle: 'Versus',
  htFlipsBody:
    'Quaeque tessera quae vertitur unum versum constat. Verbum perfectum versus summae tuae reddit, et verba longiora plus reddunt. Versibus consumptis ludus finitur.',
  htRoundTitle: 'Ambitus',
  htRoundBody:
    'Cum ultima tessera ambitus vertitur, tota tabula paulisper aperta manet. Deinde tesserae avertuntur et permiscentur, et novus ambitus incipit.',
  htLanguagesTitle: 'Linguae',
  htLanguagesBody:
    'Linguae {n}. Quaeque tabula verbis solvi potest quibus homines re vera utuntur. Verbum rarum tamen puncta fert, si lexicon id novit.',
  htKeysTitle: 'Claviatura',
  htWildTitle: 'Tesserae vagae',
  htWildBody:
    'Interdum pro littera tessera vaga apparet. Tessera vaga pro qualibet littera valet quae verbum iustum facit. Verbum iam perfectum non valet.',
  htHideTitle: 'Litterae se occultantes',
  htHideBody:
    'In gradibus difficilioribus littera aperta sua sponte reverti potest et postea in eodem circuitu redit. Horologium punctum additum accipit, rubrum. Litterae iam sumptae non revertuntur.',
  tutHideBody:
    'In gradibus difficilioribus littera aperta sua sponte reverti potest et postea in eodem circuitu redit. Litterae iam sumptae non revertuntur.',
  htSwapTitle: 'Litterae mutatae',
  htSwapBody:
    'Interdum, inter ambitus, una littera alia substituitur. Videbis quae littera sublata sit et quae addita.',
  htLevelsTitle: 'Gradus',
  htLevelEasy:
    'Eaedem duodecim litterae per totum ludum, ut eas discere et indicem verborum in animo tenere possis. Tesserae tarde vertuntur, et tabula plena satis diu apparet ut litteras tuas otiose eligas.',
  htLevelMedium:
    'Interdum littera mutatur, ita ut difficilius sit verba tenere quae postea ludere volebas. Minus temporis ad spectandum, minus ad cogitandum.',
  htLevelHard:
    'Verba trium litterarum non iam valent, et fere alterno ambitu littera mutatur. Tabula vix aperta est cum iam permiscetur.',
  htLevelInsane:
    'Omnia simul, summa celeritate. Tabula paene statim post ultimum versum permiscetur.',
  htTouchTitle: 'Quadrum tactile',
  htTouchBody:
    'Tange tesseram apertam ut litteram eius sumas. Tange quamlibet litteram quam sumpsisti ut reddas. Perfice et Dele infra tabulam sunt.',

  accountTitle: 'Ratio tua',
  menuProfile: 'Descriptio mea',
  menuGames: 'Ludi mei',
  menuPublicPage: 'Pagina mea publica',
  menuSignedInAs: 'Ingressus ut',
  signIn: 'Intra',
  signOut: 'Exi',
  signInTitle: 'Intra vel nomen da',
  signInLead: 'Ratio ludos tuos servat, in omni machina.',
  signInKeepGame: 'Intra ut hic ludus maneat, et omnes postea.',
  continueWithApple: 'Perge cum Apple',
  continueWithGoogle: 'Perge cum Google',
  signInOr: 'vel',
  emailLabel: 'Epistula',
  codeLabel: 'Nota',
  emailMeACode: 'Notam mihi mittite',
  working: 'Momentum…',
  sendNewCode: 'Novam notam mitte',
  codeSent: 'Epistulas inspice. Nota decem minuta valet.',
  notNow: 'Non nunc',
  badEmail: 'Hoc inscriptioni epistulae non simile est.',
  badCode: 'Nota illa non valuit. Notae post decem minuta pereunt et semel valent.',
  serverBusy: 'Servus adiri non potuit. Post momentum retempta.',
  serverDown: 'Servus adiri non potuit.',
  offline: 'Sine nexu',
  leaderboardTitle: 'Tabula principum',
  leaderboardEmpty: 'Nulli ludi in hac tabula adhuc. Esto primus.',
  leaderboardWouldBe: 'Ubi hic ludus staret',
  leaderboardThisGame: 'Hic ludus',
  leaderboardRanksHere: 'Ubi hic ludus stat',
  leaderboardOutside: 'Extra primos quinque.',
  tutAccountTitle: 'Serva numeros tuos',
  tutAccountBody:
    'Ratio ludos tuos servat, numeros tuos in tabulam ponit, et personam tuam custodit.',
  ssoCancelled: 'Introitus omissus est.',
  ssoExpired: 'Nimis diu duravit. Iterum intra.',
  ssoNoUsername: 'Ratio perfici non potuit. Post momentum retempta.',
  ssoFailed: 'Non valuit. Retempta, vel epistula utere.',
  tabProfile: 'Descriptio',
  countryAny: 'Malo non dicere',
  findCountry: 'Terras quaere',
  usernameLabel: 'Nomen usoris',
  bioLabel: 'De me',
  countryLabel: 'Terra',
  bioHint: 'Nulli nexus. {left}/{max}',
  save: 'Serva',
  saving: 'Servatur…',
  saved: 'Servatum.',
  nameTaken: 'Illud nomen iam aliquis habet.',
  nameTooShort: 'Paulo longius, quaeso.',
  nameTooLong: 'Nimis longum est.',
  nameBadCharacters: 'Tantum litterae, numeri, lineolae et lineolae infra.',
  nameBadEdges: 'Littera aut numero incipiat et desinat.',
  nameMixedScripts: 'Unum alphabetum elige et in eo mane.',
  nameReserved: 'Simile est nomini quod ipsi damus. Aliud elige.',
  nameUnusable: 'Illud nomen adhiberi non potest.',
  bioTooLong: 'Hic ad summum {max} litterae capiuntur.',
  bioHasLink: 'Nexus hic non licent.',
  bioHasControl: 'Litteras continet quae hic esse non possunt.',
  bioUnusable: 'Ille textus adhiberi non potest.',
  gamesHeading: 'Ludi',
  gamesEmpty: 'Nihil hic adhuc. Ludi quos ingressus ludes hic apparebunt.',
  gamesEmptyShort: 'Nihil hic adhuc.',
  gamesLoading: 'Ludi tui leguntur…',
  theirGamesLoading: 'Ludi eorum leguntur…',
  profileLoading: 'Descriptio legitur…',
  playerNotFound: 'Nemo hic illo nomine est.',
  columnWhen: 'Quando',
  columnRounds: 'ambitus',
  columnGame: 'Ludus',
  gameLoading: 'Ludus legitur…',
  gameUnreadable: 'Hic ludus legi non potuit.',
  gameNotRanked: 'Regulis mutatis lusus, itaque non ordinatur.',
  detailNotKept: 'Verba et tabulae huius ludi non iam servantur.',
  openingBoard: 'Tabula initialis',
  roundByRound: 'Per singulas vices',
  roundNothing: 'nihil inventum',
  changeReplaced: '{letter} mutata in loco {slot}',
  changeWildOn: '{letter} charta libera in loco {slot}',
  changeWildOff: '{letter} charta libera e loco {slot} abiit',
  keepThisGame: 'Hunc ludum serva',
  savedToYourGames: 'In ludis tuis servatum.',

  reportThis: 'Defer',
  reportHeading: 'Hoc defer',
  reportTheName: 'Nomen',
  reportTheBio: 'Descriptio',
  reportTheScore: 'Numerus',
  reportWhy: 'Quid in eo vitii est? Ad libitum.',
  reportSend: 'Delationem mitte',
  reportSent: 'Gratias tibi. Aliquis inspiciet.',
  reportSignIn: 'Intra ut aliquid deferas.',
  reportGone: 'Id iam non adest.',
  reportCancel: 'Desine',

  deleteAccount: 'Rationem meam dele',
  deleteAccountWhat:
    'Hoc personam tuam et omnes ludos servatos in perpetuum tollit. Revocari non potest.',
  deleteAccountAsk: 'Mitte mihi notam',
  deleteConfirm: 'In perpetuum dele',
  deleteGone: 'Ratio tua sublata est. Gratias quod lusisti.',
  deleteNoAddress:
    'Nulla inscriptio confirmata huic rationi adest, itaque deletio probari non potest. Nos conveni et manu faciemus.',

  rulesetFacts: 'Verba {letters} · nova tessera singulis {seconds}',
  plurals: {
    words: { one: 'verbum {n}', other: 'verba {n}' },
    rounds: { one: 'ambitus {n}', other: 'ambitus {n}' },
    flips: { one: 'versus {n}', other: 'versus {n}' },
    ticks: { one: 'ictus {n}', other: 'ictus {n}' },
    points: { one: 'punctum {n}', other: 'puncta {n}' },
    minLetters: { one: 'litterae {n} aut plurium', other: 'litterarum {n} aut plurium' },
    seconds: { one: 'secundis {n}', other: 'secundis {n}' },
  },
}
